import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Menu,
  Typography,
} from "@mui/material";
import MyTable from "../components/MyTable";
import PatientDetails from "../components/profile/patientProfile/PatientDetails";
import { MdReceipt, MdLocalPharmacy, MdEvent } from "react-icons/md";
import ActionButtons from "../components/profile/patientProfile/ActionButtons";
import { motion, AnimatePresence } from "framer-motion";
import { getValueFromToken } from "../utils/tokenUtils";
import {
  fetchPrescriptionsForPatient,
  fetchBillsForPatient,
  fetchAppointmentsForPatient,
  fetchInvoiceDataById,
  serviceFetchDoctorList,
} from "../services/api";
import { getClinicIdFromToken } from "../utils/tokenUtils";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { FaWhatsapp, FaEdit } from "react-icons/fa";
import FileManager from "../components/FileManager";
import { MdFolder } from "react-icons/md";
import BillingForm from "./BillingForm";
import { downloadFile, formatDateGlobal } from "../utils/commonUtils";
import { serviceSendPrescriptionDataToWhatsapp } from "../../prescription/services/api";
import { serviceSendInvoiceDataToWhatsapp } from "../services/billingApi";
import { showToast } from "../utils/toastUtils";

const PatientProfilePage = ({
  patient,
  handleCreateBill,
  handleCloseModal,
}) => {
  const [filterInput, setFilterInput] = useState("");
  const [activeTab, setActiveTab] = useState("appointments");
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [bills, setBills] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [files, setFiles] = useState([]);
  const clinic_id = getClinicIdFromToken();
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [InvoiceData, setInvoiceData] = useState({});
  const handleFilterChange = (event) => {
    const value = event.target.value || "";
    setFilterInput(value);
  };

  const handleWhatsapp = async (data) => {
    const prescriptionData = {
      prescriptionId: data.prescription_id,
    };
    const response = await serviceSendPrescriptionDataToWhatsapp(
      prescriptionData
    );
    if (response?.status) {
      showToast(response.message, "success");
    } else {
      showToast(response.message, "error");
    }
  };

  const handleInvoiceWhatsapp = async (data) => {
    const invoiceData = {
      invoiceId: data.invoice_id,
    };
    const response = await serviceSendInvoiceDataToWhatsapp(invoiceData);
    if (response?.status) {
      showToast(response.message, "success");
    } else {
      showToast(response.message, "error");
    }
  };
  const handleBillClick = async (patientId, invoiceId) => {
    const response = await fetchInvoiceDataById(invoiceId);
    if (response?.status) {
      const data = response?.data;
      setInvoiceData({
        invoice_id: data.invoice_id,
        total_amount: data.total_amount,
        paid_amount: data.paid_amount,
        discount_amount: data.discount_amount,
        service_inv: data.service,
        qty_inv: data.quantity,
      });
      setSelectedPatientId(patientId);
      setShowBillingForm(true);
    }
  };

  const handleCloseBillingForm = () => {
    setSelectedPatientId(null);
    setShowBillingForm(false);
  };

  //   const patient = {
  //     name: 'John Doe',
  //     dob: '1990-01-01',
  //     phone: '+123456789',
  //     email: 'johndoe@example.com',
  //   };

  const appointmentsColumns = React.useMemo(
    () => [
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) => formatDateGlobal(value),
      },
      {
        Header: "Doctor",
        accessor: "doctor.doctor_id",
        Cell: ({ value }) => {
          const doctor = doctors.find((doc) => doc.doctor_id === value);
          return doctor ? `${doctor.first_name} ${doctor.last_name}` : "";
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          switch (value) {
            case 0:
              return "Waiting";
            case 1:
              return "In Progress";
            case 2:
              return "Completed";
            case 3:
              return "Cancelled";
            default:
              return "Unknown";
          }
        },
      },
    ],
    [doctors]
  );

  const billsColumns = React.useMemo(
    () => [
      {
        Header: "Bill ID",
        accessor: "invoice_id",
      },
      {
        Header: "Amount",
        accessor: "total_amount",
      },
      {
        Header: "Date",
        accessor: "issued_date",
        Cell: ({ value }) => formatDateGlobal(value),
      },
      {
        Header: "Actions",
        accessor: "url",
        Cell: ({ row }) => (
          <>
            formatDateGlobal
            <IconButton
              aria-label="view"
              onClick={() => {
                window.open(row.original.url, "_blank");
              }}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="primary"
              onClick={async () => {
                const url = row?.original?.url;
                const fileName = `Invoice-${row?.original?.invoice_id}`;
                await downloadFile(url, fileName);
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="whatsapp"
              color="success"
              modal="light"
              onClick={() => handleInvoiceWhatsapp(row.original)}
            >
              <FaWhatsapp />
            </IconButton>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-2 rounded-md text-sm"
              onClick={() => {
                handleBillClick(
                  row.original.patient.patient_id,
                  row.original.invoice_id
                );
              }}
            >
              <FaEdit className="inline mr-1" />
              Edit
            </button>
          </>
        ),
      },
    ],
    []
  );

  const prescriptionsColumns = React.useMemo(
    () => [
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) => formatDateGlobal(value),
      },
      {
        Header: "Doctor",
        accessor: "doctor",
        Cell: ({ value }) =>
          `${value.doctor_qualification} ${value.doctor_speciality}`,
      },
      {
        Header: "Medications",
        accessor: "medications",
        Cell: ({ value }) => {
          const medications = JSON.parse(value);
          return medications.map((med) => med.medicine).join(", ");
        },
      },
      {
        Header: "Actions",
        accessor: "url",
        Cell: ({ row }) => (
          <>
            <IconButton
              aria-label="view"
              onClick={() => window.open(row?.original?.url, "_blank")}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="primary"
              onClick={async () => {
                const url = row?.original?.url;
                console.log("url", row?.original);
                const fileName = `Prescription-${row?.original?.prescription_id}`;
                await downloadFile(url, fileName);
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="whatsapp"
              color="success"
              modal="light"
              onClick={() => handleWhatsapp(row.original)}
            >
              <FaWhatsapp />
            </IconButton>
          </>
        ),
      },
    ],
    []
  );

  const filesColumns = React.useMemo(
    () => [
      {
        Header: "File Type",
        accessor: "file_type",
      },
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ value }) => formatDateGlobal(value),
      },
      {
        Header: "Actions",
        accessor: "url",
        Cell: ({ row }) => (
          <>
            <IconButton
              aria-label="view"
              onClick={() => window.open(row?.original?.url, "_blank")}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="primary"
              onClick={async () => {
                const url = row?.original?.url;
                const fileName = `File-${row?.original?.file_id}`;
                await downloadFile(url, fileName);
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          </>
        ),
      },
    ],
    []
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "files":
        return (
          // <MyTable
          //   columns={filesColumns}
          //   data={files}
          //   type="files"
          //   filterInput={filterInput}
          //   handleFilterChange={handleFilterChange}
          // />
          <></>
        );
      case "appointments":
        return (
          <MyTable
            columns={appointmentsColumns}
            data={appointments}
            type="appointments"
            filterInput={filterInput}
            handleFilterChange={handleFilterChange}
          />
        );
      case "bills":
        return (
          <MyTable
            columns={billsColumns}
            data={bills}
            type="bills"
            filterInput={filterInput}
            handleFilterChange={handleFilterChange}
          />
        );
      case "prescriptions":
        return (
          <MyTable
            columns={prescriptionsColumns}
            data={prescriptions}
            type="prescriptions"
            filterInput={filterInput}
            handleFilterChange={handleFilterChange}
          />
        );
      default:
        return null;
    }
  };

  const loadDoctors = async () => {
    const userId = getValueFromToken("user_id");

    try {
      const response = await serviceFetchDoctorList(userId, true);
      if (response?.status) {
        setDoctors(response?.data?.doctors);
      } else {
        console.error("Failed to fetch doctors list");
      }
    } catch (error) {
      console.error("Error fetching doctors list:", error);
    }
  };

  const fetchData = async () => {
    try {
      const prescriptionsResponse = await fetchPrescriptionsForPatient(
        patient.patient_id,
        clinic_id
      );
      const billsResponse = await fetchBillsForPatient(
        patient.patient_id,
        clinic_id
      );
      const appointmentsResponse = await fetchAppointmentsForPatient(
        patient.patient_id,
        clinic_id
      );
      // const filesResponse = await fetchFilesForPatient(patient.patient_id, clinic_id);

      // if (filesResponse?.status) {
      //   setFiles(filesResponse.data);
      // }
      if (prescriptionsResponse?.status) {
        setPrescriptions(prescriptionsResponse?.data);
      }
      if (billsResponse?.status) {
        setBills(billsResponse?.data);
      }
      if (appointmentsResponse?.status) {
        setAppointments(appointmentsResponse?.data);
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [patient.patient_id, clinic_id]);

  useEffect(() => {
    loadDoctors();
  }, []);

  const handleBookAppointemnt = () => {
    setShowAppointmentForm(!showAppointmentForm);
  };

  return (
    <div className="patient-profile-page mx-auto px-4 sm:px-8 overflow-y-auto h-full">
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          exit={{ opacity: 0, x: -50 }}
          className="flex-grow w-full lg:w-9/12"
        >
          <PatientDetails
            patient={patient}
            showAppointmentForm={showAppointmentForm}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          exit={{ opacity: 0, x: 50 }}
          className="w-full lg:w-3/12"
        >
          <ActionButtons
            handleBookAppointment={handleBookAppointemnt}
            handleCreateBill={() => handleCreateBill(patient.patient_id)}
            patientId={patient.patient_id}
            prescriptionRedirectUrl={"clinic/patient"}
            handleCloseModal={handleCloseModal}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        exit={{ opacity: 0, y: 50 }}
        className="my-6"
      >
        <nav className="flex gap-x-3 mb-0 border-b border-gray-200 px-2 py-2 bg-gray-50">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
              activeTab === "appointments"
                ? "bg-green-500 text-white border-green-500 shadow-xl"
                : "text-gray-500 bg-white border-transparent shadow-sm"
            }`}
            onClick={() => setActiveTab("appointments")}
          >
            <MdEvent className="inline mr-2" />
            Appointments
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
              activeTab === "bills"
                ? "bg-blue-500 text-white border-blue-500 shadow-xl"
                : "text-gray-500 bg-white border-transparent shadow-sm"
            }`}
            onClick={() => setActiveTab("bills")}
          >
            <MdReceipt className="inline mr-2" />
            Bills
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
              activeTab === "prescriptions"
                ? "bg-red-500 text-white border-red-500 shadow-xl"
                : "text-gray-500 bg-white border-transparent shadow-sm"
            }`}
            onClick={() => setActiveTab("prescriptions")}
          >
            <MdLocalPharmacy className="inline mr-2" />
            Prescriptions
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
              activeTab === "files"
                ? "bg-yellow-500 text-white border-yellow-500 shadow-xl"
                : "text-gray-500 bg-white border-transparent shadow-sm"
            }`}
            onClick={() => setActiveTab("files")}
          >
            <MdFolder className="inline mr-2" />
            Files
          </motion.button>
        </nav>

        <AnimatePresence>
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === "appointments" ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: activeTab === "appointments" ? -50 : 50 }}
            transition={{ duration: 0.5 }}
            className="tab-content"
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <div className="my-6">
        <FileManager patientId={patient.patient_id} appointmentId={null} />
      </div>

      {showBillingForm && (
        <BillingForm
          patientId={selectedPatientId}
          invoice_id={InvoiceData.invoice_id}
          onClose={handleCloseBillingForm}
        />
      )}
    </div>
  );
};

export default PatientProfilePage;
